/** Interview Q&A bank — merged into articles by content/index.js */

export const interviewQuestions = {
  "system-design": {
    "scalability-basics": [
      {
        "q": "Vertical vs horizontal scaling — when do you choose each?",
        "a": "Use vertical scaling early: one bigger machine is operationally simple and fine until you hit hardware ceilings or single-point-of-failure risk. Move to horizontal scaling when you need elastic capacity, fault isolation, or geographic distribution. In interviews, say you start vertical for MVP and design the app layer stateless so you can scale out without a rewrite."
      },
      {
        "q": "How do you estimate whether a system needs sharding?",
        "a": "Estimate peak QPS, working set size, and single-node limits for your DB. If one primary can comfortably handle ~5–10× projected peak with headroom, defer sharding. Shard when storage, write throughput, or index size approaches machine limits, or when hot partitions appear. Mention operational cost of resharding so you do not shard prematurely."
      },
      {
        "q": "What does \"stateless app servers\" buy you?",
        "a": "Any instance can handle any request, so load balancers can spread traffic and you can replace unhealthy nodes freely. Session state moves to cookies/JWT, Redis, or the DB. The trade-off is extra network hops for shared state and careful design of sticky requirements like WebSockets."
      },
      {
        "q": "Name three bottlenecks besides CPU.",
        "a": "Disk I/O (random reads, WAL fsync), network (cross-AZ latency, bandwidth), and lock/contention (DB row locks, single Redis key). Strong answers also mention GC pauses and thread-pool exhaustion as soft bottlenecks that look like \"CPU\" but are scheduling issues."
      }
    ],
    "back-of-envelope-estimation": [
      {
        "q": "Walk me through a back-of-envelope for a URL shortener at 100M new links/month.",
        "a": "Convert to ~40 writes/sec average; peak maybe 5–10× → ~200–400 WPS. Storage: 100M × ~500B metadata ≈ 50GB/year plus growth. Reads are usually 10–100× writes. State assumptions aloud (peak factor, payload size) and round to powers of ten — interviewers care about method more than exact digits."
      },
      {
        "q": "What numbers should every candidate memorize?",
        "a": "L1/L2/RAM/disk/network latency orders of magnitude, ~1M reads/sec from memory vs ~1–10K from disk per spindle/SSD characteristics, and rough single-machine PostgreSQL write capacity. Also 86400≈10^5 seconds/day for QPS math. Precision is less important than relative scale."
      },
      {
        "q": "How do you avoid fake precision in estimates?",
        "a": "Use 1–2 significant figures, state assumptions, and show sensitivity (\"if peak is 10× not 5×, we still fit on one primary\"). Never invent seven-digit accuracy. Tie estimates to design choices: \"this QPS means we need a cache\" vs \"a single DB is fine.\""
      }
    ],
    "cap-theorem": [
      {
        "q": "Is CAP \"pick any two\"?",
        "a": "No — that is the common misquote. In normal operation you can have consistency and availability. During a network partition you must choose between serving possibly stale/divergent data (AP) or refusing some operations to preserve consistency (CP). Partition tolerance is assumed in real multi-node systems."
      },
      {
        "q": "Would you make a payments ledger CP or AP?",
        "a": "CP (or strong consistency with careful quorum). Incorrect balances are worse than brief unavailability. Use single-leader or consensus-replicated logs, reject writes on minority partitions, and design idempotent retries. Feeds and view counts can be AP."
      },
      {
        "q": "What is PACELC and why mention it?",
        "a": "PACELC extends CAP: if Partitioned, choose A or C; Else choose Latency or Consistency. It explains why even without partitions teams pick eventual consistency for lower latency. Naming PACELC shows you design for the common case, not only failure mode slogans."
      }
    ],
    "consistency-models": [
      {
        "q": "Explain read-your-writes vs monotonic reads.",
        "a": "Read-your-writes: after I write, my subsequent reads see my write. Monotonic reads: I never see time go backwards across reads. Both are session guarantees weaker than linearizability but strong enough for many UX cases (profile edits, settings)."
      },
      {
        "q": "When is eventual consistency acceptable?",
        "a": "When stale reads are tolerable for seconds: like counts, recommendations, CDN-cached media metadata. Pair with conflict resolution (LWW, CRDTs, merge). Call out user-visible anomalies you accept so the interviewer knows you are deliberate."
      },
      {
        "q": "How do you get strong consistency on a multi-leader setup?",
        "a": "Generally you do not — multi-leader implies conflict windows. Prefer single-leader, consensus (Raft/Paxos), or carefully scoped sync replication with majority quorum. If product needs multi-region writes, isolate strongly consistent entities and keep the rest eventual."
      }
    ],
    "availability": [
      {
        "q": "How do you calculate availability from nines?",
        "a": "99.9% ≈ 8.7 hours downtime/year; 99.99% ≈ 52 minutes. Availability of serial dependencies multiplies (0.99×0.99=0.9801). Parallel redundant paths improve availability. Show you design for failure budgets, not mythical 100%."
      },
      {
        "q": "What improves availability more: retries or redundancy?",
        "a": "Redundancy (multi-AZ, replicas, health-checked LB) removes single points of failure. Retries help transient errors but can amplify outages without backoff/jitter and idempotency. Mature answers combine both plus circuit breakers."
      },
      {
        "q": "How do you talk about SLA vs SLO vs SLI?",
        "a": "SLI is the measured signal (success rate, latency p99). SLO is the target (99.9% success). SLA is the contractual promise with consequences. In design interviews, propose SLOs that drive capacity and failover choices."
      }
    ],
    "replication": [
      {
        "q": "Sync vs async replication trade-offs?",
        "a": "Sync waits for replica ack → stronger durability/consistency, higher write latency, more sensitivity to slow replicas. Async is fast and available but risks data loss on primary failure and read-your-writes issues if reading replicas. Many systems use semi-sync as a middle ground."
      },
      {
        "q": "How do you handle failover?",
        "a": "Health checks detect primary death, elect a new primary (consensus or orchestration), redirect clients (DNS/VIP/proxy), and fence the old primary (STONITH) to avoid split brain. Mention replica lag checks so you do not promote a far-behind node blindly."
      },
      {
        "q": "Why can replicas still serve stale reads?",
        "a": "Replication delay. Mitigate with read-your-writes routing to primary, session stickiness, or causal tokens. Quantify acceptable lag in the interview."
      }
    ],
    "partitioning": [
      {
        "q": "Hash vs range partitioning?",
        "a": "Hash spreads load evenly and avoids hot ranges but hurts range scans. Range keeps locality for time/ID scans but risks hotspots (all writes to latest partition). Compound strategies (hash of user, range within) are common."
      },
      {
        "q": "How do you reshard with minimal downtime?",
        "a": "Dual-write or change-data-capture to new topology, backfill, verify checksums, then cut reads gradually. Consistent hashing reduces remapped keys. Call out dual-write idempotency and rollback plan."
      },
      {
        "q": "What is a hotspot and how do you fix it?",
        "a": "A partition receiving disproportionate traffic (celebrity user, current date key). Fix with salting keys, separate hot-key caches, or splitting the partition. Detect via per-shard metrics."
      }
    ],
    "consistent-hashing": [
      {
        "q": "Why not modulo hashing for caches?",
        "a": "Adding/removing a node remaps almost all keys (key % N changes). Consistent hashing remaps ~1/N keys, reducing cache stampedes during scaling. Virtual nodes improve balance."
      },
      {
        "q": "What are virtual nodes?",
        "a": "Each physical node owns many points on the hash ring so load distributes more evenly and heterogeneous capacities can be weighted. Trade-off is more metadata and slightly more hop complexity."
      },
      {
        "q": "Where else is consistent hashing used?",
        "a": "Distributed caches, Dynamo-style stores, some CDN and load-balancing schemes, and shard routers. Mention it when designing sticky routing without a central map."
      }
    ],
    "quorum-and-consensus": [
      {
        "q": "Explain R + W > N.",
        "a": "With N replicas, if read quorum R and write quorum W satisfy R+W>N, intersecting sets guarantee a read sees the latest successful write (under common assumptions). Example N=3,W=2,R=2. Tuning W/R trades latency vs durability/freshness."
      },
      {
        "q": "Raft vs quorum in Cassandra-style systems?",
        "a": "Raft/Paxos elect a leader and replicate a log for strong consistency/consensus on operations. Quorum RW is a per-key consistency mechanism without a single global leader. Do not conflate them in interviews."
      },
      {
        "q": "When is majority quorum not enough?",
        "a": "Clock skew with LWW, partial writes, or clients reading without quorum. Also Byzantine faults need different protocols. Mention fencing tokens for leader leases."
      }
    ],
    "leader-election": [
      {
        "q": "How would you elect a leader in production?",
        "a": "Prefer battle-tested coordination: ZooKeeper/etcd/Consul via leases, or consensus libraries (Raft). DIY \"lowest IP wins\" breaks under partitions. Leaders should hold a lease and heartbeat; followers must fence expired leaders."
      },
      {
        "q": "What is split brain?",
        "a": "Two nodes both believe they are primary and accept writes, diverging data. Prevent with fencing, quorum, or external locks. Detect via epoch numbers and reject stale leaders."
      },
      {
        "q": "Leader vs leaderless designs?",
        "a": "Leader simplifies ordering and conflicts; leaderless improves write availability across regions at the cost of conflict resolution. Pick based on consistency needs."
      }
    ],
    "dns-and-anycast": [
      {
        "q": "How does DNS fit into system design?",
        "a": "DNS maps names to VIP/LB IPs and is your coarsest routing lever (geo DNS, weighted records). Cache TTLs trade failover speed vs lookup load. Anycast advertises the same IP from many PoPs so BGP routes users to a nearby edge."
      },
      {
        "q": "Why is DNS failover slow?",
        "a": "Client and resolver TTLs delay switching after you change records. Use low TTL carefully (more query load), health-checked LBs inside a stable VIP, or anycast for faster regional failover."
      },
      {
        "q": "DNS vs service discovery inside a VPC?",
        "a": "Public DNS for user-facing endpoints; inside the mesh use Consul/Kubernetes DNS/ENV for rapidly changing instance sets. Mixing them causes stale targets."
      }
    ],
    "cdn": [
      {
        "q": "What should live on a CDN?",
        "a": "Static assets, images, video segments, and cacheable API GETs with careful TTLs. Do not cache personalized or sensitive responses at the edge without Vary rules. Origin shield and tiered caching protect origins."
      },
      {
        "q": "How do you invalidate CDN content?",
        "a": "Purge by URL/tag, versioned filenames (cache forever), or short TTLs for volatile content. Prefer versioning over emergency purges at scale."
      },
      {
        "q": "CDN vs app-cache Redis?",
        "a": "CDN is geographic and HTTP-oriented; Redis is application-level, fine-grained, and usually regional. Many designs use both: CDN for bytes, Redis for computed objects."
      }
    ],
    "load-balancer": [
      {
        "q": "L4 vs L7 load balancing?",
        "a": "L4 routes on IP/port (fast, TLS passthrough possible). L7 understands HTTP: path-based routing, header sticky, WAF, retries. Most public web entry points are L7; east-west may be L4 or mesh."
      },
      {
        "q": "How do you avoid thundering herds on unhealthy nodes?",
        "a": "Active health checks, slow-start when adding nodes, connection draining, and outlier ejection. Pair with client-side retries that have budgets."
      },
      {
        "q": "Sticky sessions — yes or no?",
        "a": "Avoid if you can externalize state. Use stickiness only for WebSockets or hard affinity, knowing failover will drop affinity and capacity can skew."
      }
    ],
    "reverse-proxy": [
      {
        "q": "Reverse proxy vs load balancer?",
        "a": "A reverse proxy terminates client connections and forwards to upstreams; load balancing is one job it may do. Proxies also add TLS, compression, auth, and caching (nginx, Envoy)."
      },
      {
        "q": "Why put nginx/Envoy in front of app servers?",
        "a": "Centralize TLS, rate limits, request buffering, and observability. Keep app servers focused on business logic and scale them independently."
      },
      {
        "q": "What can go wrong?",
        "a": "Misconfigured timeouts masking app failures, buffering large uploads in memory, and double-gzip. Align proxy and app timeouts deliberately."
      }
    ],
    "api-gateway": [
      {
        "q": "What belongs in an API gateway?",
        "a": "Authn/authz edge checks, rate limiting, request routing/aggregation, API keys, and protocol translation. Keep business logic in services — gateways that become god objects are hard to change."
      },
      {
        "q": "Gateway vs service mesh?",
        "a": "Gateway is north-south entry. Mesh (sidecar/eBPF) handles east-west mTLS, retries, and traffic policy. Many orgs use both with clear boundaries."
      },
      {
        "q": "BFF pattern?",
        "a": "Backend-for-frontend: a gateway/experience layer tailored per client (mobile vs web) to avoid over-fetching. Mention when mobile needs different payloads than web."
      }
    ],
    "rate-limiting": [
      {
        "q": "Token bucket vs sliding window?",
        "a": "Token bucket allows short bursts (tokens refill). Sliding window smooths more accurately over the last N seconds but can be costlier. Fixed window is simple but bursty at boundaries."
      },
      {
        "q": "Where do you enforce limits?",
        "a": "At the edge/gateway for coarse IP/user limits, and in services for expensive operations. Distributed counters need Redis/Dynamo with careful atomicity."
      },
      {
        "q": "How do you rate limit fairly across tenants?",
        "a": "Per-tenant quotas, weighted fair sharing, and separate burst vs sustained limits. Return 429 with Retry-After and make clients idempotent."
      }
    ],
    "caching-strategies": [
      {
        "q": "Cache-aside vs write-through?",
        "a": "Cache-aside: app reads DB on miss, then populates cache; writes update DB and invalidate/update cache. Write-through: writes go to cache and DB together — simpler read path, slower writes. Write-behind batches DB writes for throughput at durability risk."
      },
      {
        "q": "How do you prevent cache stampede?",
        "a": "Soft TTLs with probabilistic early refresh, singleflight/locks on miss, and staggered expirations. Also cache negative lookups briefly."
      },
      {
        "q": "What do you cache?",
        "a": "Hot keys with high read/write ratio, expensive joins, and computed feeds. Avoid rapidly changing strong-consistency data unless you have a clear invalidation story."
      }
    ],
    "cache-aside-and-write-through": [
      {
        "q": "Walk through cache-aside read/write.",
        "a": "Read: get from cache; on miss read DB, set cache, return. Write: write DB, then delete (prefer) or update cache. Deleting is safer under concurrent writers to avoid stale sets."
      },
      {
        "q": "When is write-through better?",
        "a": "When read path must always hit warm cache and write volume is moderate. Used in some CDN origin shields and read-heavy product catalogs."
      },
      {
        "q": "How do you keep cache coherent across regions?",
        "a": "Usually you do not aim for strict coherence — use regional caches, short TTLs, or pub/sub invalidation. For global strong reads, skip cache or use version tokens."
      }
    ],
    "redis": [
      {
        "q": "Redis vs Memcached?",
        "a": "Redis: rich structures, persistence options, pub/sub, Lua, single-threaded command execution. Memcached: simple distributed RAM cache, multi-threaded, great for blunt object caching. Pick Redis when you need data structures or atomic ops."
      },
      {
        "q": "How does Redis handle failover?",
        "a": "Redis Sentinel or Cluster manages replica promotion. Clients must rediscover the primary. Understand persistence (AOF/RDB) vs pure cache semantics — data loss policies differ."
      },
      {
        "q": "Hot key problem in Redis?",
        "a": "One key dominates QPS on one shard. Mitigate with local caching, key splitting, or read replicas for that key. Mentions of MONITOR/slowlog show ops maturity."
      }
    ],
    "memcached": [
      {
        "q": "When choose Memcached over Redis?",
        "a": "Simple volatile object cache, very high QPS, multi-threaded hosts, and you do not need persistence or complex types. Smaller operational surface for pure caching."
      },
      {
        "q": "How does consistent hashing apply?",
        "a": "Client libraries map keys to servers with consistent hashing so node changes remap few keys. Be aware of slab allocator fragmentation historically."
      },
      {
        "q": "Failure mode?",
        "a": "Node loss = cache miss storm to DB. Mitigate with replica caches, request coalescing, and capacity headroom on origins."
      }
    ],
    "bloom-filters": [
      {
        "q": "What is a Bloom filter good for?",
        "a": "Probabilistic set membership with tiny memory: \"definitely not present\" or \"maybe present.\" Great before expensive disk lookups (BigTable, caches, URL crawl seen-set). False positives exist; false negatives do not (in standard Bloom)."
      },
      {
        "q": "Why not a HashSet?",
        "a": "HashSet stores full keys — huge for billions of entries. Bloom uses a bit array + hashes. Trade exactness for memory."
      },
      {
        "q": "How do you size it?",
        "a": "Choose expected n and target false-positive rate; that determines bits and hash count. Undersizing blows up FPR and wastes the optimization."
      }
    ],
    "message-queues": [
      {
        "q": "When do you introduce a queue?",
        "a": "To absorb spikes, decouple producers from slow consumers, and enable retries/async workflows (email, image processing). Do not use queues as a DB — persist business state explicitly."
      },
      {
        "q": "At-least-once vs exactly-once?",
        "a": "Most brokers give at-least-once; exactly-once needs idempotent consumers or transactional outbox + dedupe. Explain your dedupe key strategy."
      },
      {
        "q": "Queue vs pub/sub?",
        "a": "Queue: competing consumers share work. Pub/sub: each subscription gets a copy. Many products offer both (SNS+SQS, Kafka consumer groups)."
      }
    ],
    "pub-sub": [
      {
        "q": "Fan-out design for notifications?",
        "a": "Publisher emits an event; multiple subscriber groups (push, email, analytics) consume independently. Isolate failure domains so a slow email worker does not block push."
      },
      {
        "q": "Ordering guarantees?",
        "a": "Global order is expensive. Prefer per-key ordering (partition key = userId). Document what order you do and do not promise."
      },
      {
        "q": "How do subscribers scale?",
        "a": "Partitioned topics + consumer groups. Lag metrics drive autoscaling. Warn about hot partitions."
      }
    ],
    "kafka": [
      {
        "q": "Why Kafka over a classic queue?",
        "a": "Durable partitioned log, high throughput, replay, and multiple consumer groups reading the same data. Classic queues delete on ack and struggle with fan-out replay."
      },
      {
        "q": "How does a consumer group work?",
        "a": "Partitions are assigned across group members; each partition has one active consumer in the group. Rebalance on membership change — design for stop-the-world pauses or use cooperative rebalancing."
      },
      {
        "q": "What is ISR?",
        "a": "In-sync replicas that are caught up for a partition. acks=all waits for ISR — durability vs latency trade-off. Under-replicated partitions are a paging signal."
      }
    ],
    "rabbitmq": [
      {
        "q": "When is RabbitMQ a better fit than Kafka?",
        "a": "Complex routing (exchanges/bindings), lower-volume task queues, per-message ACKs, and flexible work queues. Kafka shines for event logs and replayable streams."
      },
      {
        "q": "How do you avoid message loss?",
        "a": "Persistent messages, durable queues, publisher confirms, and consumer manual acks after side effects. Prefetch limits protect consumers."
      },
      {
        "q": "Poison messages?",
        "a": "Retry with backoff then dead-letter. Do not infinite-requeue without a DLQ strategy."
      }
    ],
    "idempotency": [
      {
        "q": "How do you make a payment API idempotent?",
        "a": "Client sends Idempotency-Key; server stores request hash + result for a TTL. Retries return the same response without double charge. Persist the key in the same transaction as the side effect when possible."
      },
      {
        "q": "At-least-once delivery implies what for consumers?",
        "a": "Consumers must tolerate duplicates: unique constraints, upserts, or dedupe tables. Never assume \"processed once\" from the broker alone."
      },
      {
        "q": "Idempotency vs exactly-once processing?",
        "a": "Exactly-once is an end-to-end property (produce + process + side effects). Idempotent consumers are the practical path in most microservice architectures."
      }
    ],
    "retry-strategies": [
      {
        "q": "Why exponential backoff + jitter?",
        "a": "Synchronized retries amplify outages (thundering herd). Exponential backoff reduces rate; jitter spreads load. Cap attempts and use a dead-letter path."
      },
      {
        "q": "What should not be retried?",
        "a": "Most 4xx (except 409/429 sometimes), auth failures, and non-idempotent ops without keys. Retry 5xx and network timeouts with budgets."
      },
      {
        "q": "Retry where — client, proxy, or worker?",
        "a": "One layer should own retries for a hop to avoid multiplicative storms. Document budgets end-to-end."
      }
    ],
    "dead-letter-queues": [
      {
        "q": "What is a DLQ for?",
        "a": "Messages that exhaust retries or fail validation land in a DLQ for inspection, alerting, and controlled replay. Without DLQs, poison pills block partitions/queues."
      },
      {
        "q": "How do you operate a DLQ?",
        "a": "Alert on depth, triage root cause, fix consumer or data, then replay. Version poison payloads so bad formats do not re-break."
      },
      {
        "q": "DLQ vs dropping messages?",
        "a": "Dropping loses data silently. DLQ preserves for audit — essential in payments/notifications."
      }
    ],
    "storage-overview": [
      {
        "q": "How do you choose SQL vs NoSQL vs object storage?",
        "a": "SQL for relational integrity and complex queries; NoSQL for flexible docs, wide access patterns, or massive scale with known keys; object storage for large blobs. Start from access patterns, not hype."
      },
      {
        "q": "What questions do you ask before picking a store?",
        "a": "Read/write QPS, item size, query patterns (key lookup vs ad hoc), consistency needs, and ops skill. Prototype the hottest query."
      },
      {
        "q": "Polyglot persistence — yes?",
        "a": "Yes when justified (search index + OLTP + blob). Each store adds failure modes — justify with a clear workload."
      }
    ],
    "sql": [
      {
        "q": "When does SQL become the bottleneck?",
        "a": "Write contention on hot rows, large joins without indexes, single-primary throughput limits, and operational vacuum/DDL pain. Mitigate with indexes, CQRS read replicas, then sharding."
      },
      {
        "q": "Normalized vs denormalized for HLD?",
        "a": "Normalize for write integrity; denormalize/caches for read latency. In design interviews, show both and when you transition."
      },
      {
        "q": "Transactions across microservices?",
        "a": "Avoid distributed 2PC when possible. Use sagas/outbox. Keep a single DB transaction inside one service boundary."
      }
    ],
    "nosql": [
      {
        "q": "Name three NoSQL families.",
        "a": "KV (Redis, DynamoDB), document (Mongo), wide-column (Cassandra), plus graph. Match family to access pattern: primary key lookups vs relationships."
      },
      {
        "q": "What does \"design for queries\" mean?",
        "a": "In Cassandra/Dynamo you model tables per query, accepting denormalization. Wrong partition key = hot partitions or scatter-gather."
      },
      {
        "q": "Consistency knobs?",
        "a": "Per-request quorum, tunable consistency, and conditional writes. Explain what your app does on conflict."
      }
    ],
    "object-storage": [
      {
        "q": "Why S3-style storage for media?",
        "a": "Cheap durable blobs, virtually infinite scale, lifecycle policies, and CDN integration. Metadata stays in a DB; objects in bucket keyed by content hash/id."
      },
      {
        "q": "Consistency of object stores?",
        "a": "Modern S3 is strongly consistent for new puts of existing keys in most cases — still design uploads with unique keys and finalize records after successful put."
      },
      {
        "q": "Multipart upload use case?",
        "a": "Large videos: parallel parts, retry per part, complete atomically. Essential for unreliable clients."
      }
    ],
    "elasticsearch": [
      {
        "q": "ES vs OLTP DB for search?",
        "a": "ES is an inverted index for relevance, filters, and aggregations — not your source of truth. Sync from primary via CDC. Expect eventual consistency and reindex strategies."
      },
      {
        "q": "Mapping explosion risk?",
        "a": "Unbounded dynamic fields blow memory. Use strict mappings and flattened fields for user-defined keys."
      },
      {
        "q": "How do you keep search fresh?",
        "a": "Near-real-time refresh intervals, CDC pipelines, and versioned indices with alias swaps for reindexes."
      }
    ],
    "sharding": [
      {
        "q": "Shard key selection criteria?",
        "a": "High cardinality, even access, and alignment with query predicates so most queries hit one shard. Avoid monotonically increasing keys without salting."
      },
      {
        "q": "Cross-shard transactions?",
        "a": "Hard — avoid. Keep transactional boundaries within a shard (e.g., shard by accountId so account ops are local)."
      },
      {
        "q": "How do you detect bad sharding?",
        "a": "Per-shard CPU/QPS/storage dashboards and p99 skew. Rebalance before paging."
      }
    ],
    "secondary-indexes": [
      {
        "q": "Local vs global secondary indexes?",
        "a": "Local indexes live with the partition (cheap writes, partition-scoped queries). Global indexes are separate and can query across partitions but add write amplification and consistency lag."
      },
      {
        "q": "Why can indexes slow writes?",
        "a": "Each write updates multiple index structures. Over-indexing is a common production footgun — index for proven queries."
      },
      {
        "q": "Covering indexes?",
        "a": "Index includes all columns needed so the query avoids heap lookups. Mention in SQL performance answers."
      }
    ],
    "tinyurl-url-shortener": [
      {
        "q": "How do you generate short codes?",
        "a": "Counter + base62, hash+truncate with collision retry, or pre-generated ranges per service instance. Discuss uniqueness, predictability (security), and length vs capacity (62^7 etc.)."
      },
      {
        "q": "Read path at scale?",
        "a": "Cache hot redirects in Redis/CDN; DB holds canonical mapping. 301 vs 302 affects caching — call it out."
      },
      {
        "q": "Custom aliases and abuse?",
        "a": "Reserve collision checks, rate limit creation, and scan for phishing. Separate analytics pipeline so redirects stay fast."
      }
    ],
    "rate-limiter-service": [
      {
        "q": "Design a distributed rate limiter.",
        "a": "API: check(key, cost). Store counters in Redis with token bucket/sliding window via Lua for atomicity. Place it synchronously on the request path with tight SLOs and fail-open vs fail-closed policy."
      },
      {
        "q": "Per-user vs per-IP?",
        "a": "Both — IP for anonymity, user/API key for authenticated quotas. Hierarchical limits (tenant → user → endpoint)."
      },
      {
        "q": "Where does it sit in the diagram?",
        "a": "Usually at gateway/edge before expensive services. Mention local in-process limiters as first line with Redis as global coordinator."
      }
    ],
    "notification-service": [
      {
        "q": "How do you fan out millions of notifications?",
        "a": "Ingest to a queue, preference service filters channels, workers per channel (push/email/SMS) with rate limits per provider. Template service separates content from delivery."
      },
      {
        "q": "Read vs write fanout for notifications?",
        "a": "Write fanout precomputes per-user inbox (fast read, heavy write for celebs). Read fanout computes on read. Hybrid is common for feeds/notifications."
      },
      {
        "q": "Reliability?",
        "a": "At-least-once with idempotent provider APIs, DLQs, and user-visible delivery status. Respect quiet hours and unsubscribe."
      }
    ],
    "news-feed": [
      {
        "q": "Fan-out on write vs read?",
        "a": "Write: push to followers' timelines (fast read, costly for celebs). Read: pull from followees at read time. Hybrid: push for normal users, pull for celebrities."
      },
      {
        "q": "How do you rank a feed?",
        "a": "Candidate generation → ranking model/heuristics → filters. Precompute rough timelines and re-rank top N online."
      },
      {
        "q": "Caching strategy?",
        "a": "Cache per-user home timeline fragments in Redis; invalidate on new posts selectively. CDN unsuitable for personalized feeds."
      }
    ],
    "instagram": [
      {
        "q": "Photo upload path?",
        "a": "Client → upload service → object storage; async workers create renditions; metadata DB stores pointers; feed/fanout async. Never put blobs in SQL."
      },
      {
        "q": "Feed for a celebrity?",
        "a": "Do not fan out to 100M followers synchronously — use pull or hybrid. Celebrity posts land in a celebrity store queried at read."
      },
      {
        "q": "Hotspot counters (likes)?",
        "a": "Sharded counters, approximate counts, or write-behind aggregation. Strong exactness is expensive."
      }
    ],
    "twitter-x": [
      {
        "q": "Home timeline vs user timeline?",
        "a": "User timeline is posts by one user (easy query). Home is merged from followees — the hard fanout problem. Search/trends are separate systems."
      },
      {
        "q": "How do you handle trending topics?",
        "a": "Stream processing on post firehose, sliding windows, anomaly detection, and caching top lists. Expect approximate counts."
      },
      {
        "q": "Tweet idempotency and retries?",
        "a": "Client-generated UUIDs and dedupe so mobile retries do not double-post."
      }
    ],
    "whatsapp": [
      {
        "q": "Online messaging architecture?",
        "a": "Persistent connections (WebSocket/TCP) to connection gateways; session mapping user→server; messages durable in queues/stores; delivery receipts. End-to-end encryption adds key management constraints."
      },
      {
        "q": "Group message fanout?",
        "a": "Server expands group membership and delivers per device; large groups need careful fanout and rate limits. Store per-user inbox pointers."
      },
      {
        "q": "Media messages?",
        "a": "Upload media to blob store, send message with pointer; receivers download separately. Encrypt media keys as part of E2EE design."
      }
    ],
    "dropbox": [
      {
        "q": "How do you sync files efficiently?",
        "a": "Chunk files, content-hash chunks, upload only missing chunks, and maintain metadata namespace/versioning. Conflict resolution for concurrent edits."
      },
      {
        "q": "Metadata vs block servers?",
        "a": "Metadata DB for namespace, permissions, versions; block/chunk store for bytes. Clients talk to both with auth tokens."
      },
      {
        "q": "Notification of changes?",
        "a": "Long poll/websocket from notification service so clients pull deltas quickly without hammering metadata."
      }
    ],
    "google-drive": [
      {
        "q": "Permissions model?",
        "a": "ACL/inheritance on folders, sharing links, and consistency of permission checks on every read. Cache permissions carefully with invalidation."
      },
      {
        "q": "Collaboration docs vs binary files?",
        "a": "Binary: chunk sync like Dropbox. Collaborative editors need OT/CRDT and different storage. Clarify scope in the interview."
      },
      {
        "q": "Version history?",
        "a": "Keep immutable versions/chunks with retention policies; metadata points to version DAG."
      }
    ],
    "youtube": [
      {
        "q": "Upload and processing pipeline?",
        "a": "Upload to blob store → transcoding workers produce renditions/bitrates → CDN distribution → metadata DB. Processing is async with progress events."
      },
      {
        "q": "Adaptive bitrate streaming?",
        "a": "Client picks segment bitrate from a manifest based on bandwidth. CDN caches segments globally."
      },
      {
        "q": "Thumbnails and recommendations?",
        "a": "Separate systems: feature pipelines and candidate generation. Do not block watch path on ML."
      }
    ],
    "netflix": [
      {
        "q": "Open connect / CDN strategy?",
        "a": "Push popular titles to ISP caches close to users. Origin for long-tail. Playback starts from nearest cache."
      },
      {
        "q": "Personalization at scale?",
        "a": "Precompute rows/candidates offline; online rank small sets. Chaos and canaries for resiliency culture — mention if relevant."
      },
      {
        "q": "Why microservices here?",
        "a": "Independent scaling of billing, streaming, and recommendations with failure isolation. Discuss API gateway and resilience patterns."
      }
    ],
    "ride-sharing": [
      {
        "q": "How do you match riders and drivers?",
        "a": "Geo-index drivers (geohash/quadtree), dispatch service scores ETA/price, offer with timeouts. Separate location stream (high write) from trip state machine."
      },
      {
        "q": "Location update rate?",
        "a": "Drivers send frequent GPS updates to a location service/in-memory grid; riders subscribe nearby. Downsample storage for history."
      },
      {
        "q": "Surge pricing data needs?",
        "a": "Demand/supply per geohash windows — stream aggregation, not OLTP queries on raw pings."
      }
    ],
    "payment-gateway": [
      {
        "q": "How do you avoid double charges?",
        "a": "Idempotency keys, ledger append-only entries, and state machine (created→authorized→captured). Reconcile with PSP webhooks that are also idempotent."
      },
      {
        "q": "Consistency requirements?",
        "a": "Strong for balances/ledgers. Use single-DB transactions per account shard and careful saga only across org boundaries."
      },
      {
        "q": "PCI considerations at high level?",
        "a": "Minimize card data scope, tokenize via PSP, encrypt in transit, audit logs. Show you will not store PANs casually."
      }
    ],
    "search-engine": [
      {
        "q": "Crawl → index → serve pipeline?",
        "a": "Crawler frontier with politeness → document store → indexing pipeline builds inverted index → query service retrieves/ranks. Freshness vs cost trade-offs."
      },
      {
        "q": "How does an inverted index work?",
        "a": "Term → postings list of doc IDs. Query intersects/unions postings, then ranks. Sharding by term or doc has different fanout costs."
      },
      {
        "q": "Ranking signals?",
        "a": "Text relevance, freshness, authority/links, personalization. Keep serving path reliant on precomputed features when possible."
      }
    ],
    "web-crawler": [
      {
        "q": "Politiness and politeness budgets?",
        "a": "Per-host rate limits, robots.txt respect, and crawl-delay. Frontier prioritizes URL importance without melting origin sites."
      },
      {
        "q": "URL frontier data structure?",
        "a": "Priority queues per host, bloom/seen-set for dedupe, and persistent queues for scale. Normalize URLs carefully."
      },
      {
        "q": "Distributed crawler coordination?",
        "a": "Shard by host hash so one worker owns a host's politeness. Shared seen-set via bloom + remote store."
      }
    ]
  },
  "lld": {
    "oop-fundamentals": [
      {
        "q": "How do you go from requirements to classes?",
        "a": "Underline nouns as candidate classes, verbs as methods, and adjectives as states. Filter out classes without behavior. Draw a quick CRC or UML sketch before coding. Interviewers watch whether you clarify scope first."
      },
      {
        "q": "Object vs class?",
        "a": "A class is the blueprint; an object is a runtime instance with its own state. In LLD, talk about instance multiplicity (one ElevatorSystem, many Elevator cars)."
      },
      {
        "q": "What makes a good class boundary?",
        "a": "High cohesion (one reason to change) and low coupling. If two classes always change together, maybe merge; if one class has unrelated methods, split."
      }
    ],
    "encapsulation-and-abstraction": [
      {
        "q": "Encapsulation vs abstraction?",
        "a": "Encapsulation hides state behind methods (protect invariants). Abstraction exposes a simple interface while hiding complexity (interface/API). You need both: private fields + clear public methods."
      },
      {
        "q": "Give an example of broken encapsulation.",
        "a": "Public mutable fields like `user.balance += 10` bypass validation. Prefer `user.credit(10)` that checks limits and emits events."
      },
      {
        "q": "How does this show up in LLD interviews?",
        "a": "Getters/setters alone are not encapsulation. Show invariants: parking spot can only be free or occupied via park/leave methods."
      }
    ],
    "inheritance": [
      {
        "q": "When is inheritance the wrong tool?",
        "a": "When you need optional behaviors or multiple dimensions of variation — prefer composition/strategy. Inheritance couples you to the parent forever and breaks with deep hierarchies."
      },
      {
        "q": "Liskov Substitution in one example.",
        "a": "If code expects Bird.fly(), Penguin should not be a Bird subtype that throws. Model Flyable separately or use composition."
      },
      {
        "q": "Inheritance vs interface implementation?",
        "a": "Classes inherit implementation; interfaces declare capabilities. Prefer coding to interfaces for testability and swapping implementations."
      }
    ],
    "composition-over-inheritance": [
      {
        "q": "Why do interviewers push composition?",
        "a": "It favors has-a relationships you can rewire at runtime (inject strategies) without brittle parent classes. Easier testing via mocks."
      },
      {
        "q": "Refactor an inheritance smell.",
        "a": "Replace subclass overrides with a Behavior/Strategy field. Example: Duck with FlyBehavior rather than FlyableDuck/RubberDuck hierarchy hell."
      },
      {
        "q": "Does composition mean never inherit?",
        "a": "No — use inheritance for true \"is-a\" shared identity with stable contracts. Default to composition when unsure."
      }
    ],
    "solid": [
      {
        "q": "Explain SRP with a service class.",
        "a": "OrderService should not send email and charge cards and write reports. Split Payment, Notification, Reporting. One class, one reason to change."
      },
      {
        "q": "How do you show OCP in machine coding?",
        "a": "New pricing rule = new class implementing PriceRule, registered in a list — not another if-else in checkout."
      },
      {
        "q": "Dependency Inversion in practice?",
        "a": "High-level ElevatorController depends on Door interface, not SpecificMotorDoor. Wire concrete types in main/factory."
      }
    ],
    "uml": [
      {
        "q": "Which UML diagrams matter in interviews?",
        "a": "Class diagram for structure, sequence for a key flow (park car, place order). Skip exotic diagrams unless asked. Clarity beats completeness."
      },
      {
        "q": "How do you show relationships?",
        "a": "Association, aggregation/composition, inheritance, dependency. Mention multiplicity (1, 1..*, 0..1)."
      },
      {
        "q": "Sequence diagram tip?",
        "a": "Pick one happy path + one failure path. Label messages with method names matching your classes."
      }
    ],
    "factory": [
      {
        "q": "Simple vs factory method vs abstract factory?",
        "a": "Simple: static/method returns concrete type. Factory method: subclass decides product. Abstract factory: families of products. In interviews, simple factory often suffices — say when you would upgrade."
      },
      {
        "q": "When not to use a factory?",
        "a": "When there is one concrete type and no creation logic — a factory adds noise."
      },
      {
        "q": "Example in parking lot?",
        "a": "VehicleFactory.create(type) returns Car/Bike/Truck with correct size — keeps ParkingService free of switches everywhere."
      }
    ],
    "builder": [
      {
        "q": "Builder vs telescoping constructors?",
        "a": "Builder makes optional fields readable and validates at build(). Great for HTTP clients, query objects, and game entities with many params."
      },
      {
        "q": "When is Builder overkill?",
        "a": "Two-field classes. Prefer constructors or records."
      },
      {
        "q": "Is Builder thread-safe?",
        "a": "Usually build a new instance per use; do not share a half-built builder across threads."
      }
    ],
    "singleton": [
      {
        "q": "Why are interviewers wary of Singleton?",
        "a": "Hidden global state, hard tests, and concurrency bugs. Prefer DI of a single shared instance from composition root."
      },
      {
        "q": "If you must implement one, how?",
        "a": "Enum singleton in Java, or lazy holder / double-checked locking with volatile. Discuss classloader issues."
      },
      {
        "q": "Acceptable singleton uses?",
        "a": "True process-wide resources (logger config gateways) — still inject interfaces when possible."
      }
    ],
    "adapter": [
      {
        "q": "Adapter vs facade?",
        "a": "Adapter makes incompatible interfaces work together (wrap legacy). Facade simplifies a subsystem with a new narrow API — not necessarily translating an existing interface."
      },
      {
        "q": "Real example?",
        "a": "Wrap a third-party payments SDK behind CompanyPaymentPort so you can swap providers."
      },
      {
        "q": "Object vs class adapter?",
        "a": "Object adapter uses composition (preferred). Class adapter uses multiple inheritance where available."
      }
    ],
    "decorator": [
      {
        "q": "Decorator vs inheritance for features?",
        "a": "Decorator stacks behaviors at runtime (buffered + compressed stream) without subclass explosion."
      },
      {
        "q": "How does it differ from proxy?",
        "a": "Decorator adds responsibilities; proxy controls access (lazy, remote, auth). Structures look similar — intent differs."
      },
      {
        "q": "Example?",
        "a": "Coffee = Espresso; coffee = new Milk(new Mocha(coffee)); cost/description accumulate."
      }
    ],
    "facade": [
      {
        "q": "When to introduce a facade?",
        "a": "Client must orchestrate many subsystem types. Facade offers one method like `checkout()` hiding inventory, payment, notify."
      },
      {
        "q": "Facade vs god class?",
        "a": "Facade delegates; god class contains all logic. Keep facade thin."
      },
      {
        "q": "Interview use?",
        "a": "Good for outlining LLD: show a service facade then drill into collaborators."
      }
    ],
    "proxy": [
      {
        "q": "Types of proxies?",
        "a": "Virtual (lazy), protection (auth), remote (RPC stub), logging/metrics. Explain which one you need."
      },
      {
        "q": "Proxy vs decorator again?",
        "a": "Proxy often same interface and may skip real subject; decorator always adds behavior and usually calls wrapped component."
      },
      {
        "q": "Example in systems?",
        "a": "Hibernate lazy-loading proxy; API gateway as protection proxy at system level (conceptual)."
      }
    ],
    "observer": [
      {
        "q": "Observer vs pub/sub?",
        "a": "Observer is usually in-process (subject notifies observers). Pub/sub often brokered/distributed. Same idea, different scale."
      },
      {
        "q": "Memory leak risk?",
        "a": "Forgot to unsubscribe — observers pin subjects. Discuss weak refs or explicit detach."
      },
      {
        "q": "Order of notification?",
        "a": "Do not rely on order unless documented. Observers should be quick or async to avoid blocking the subject."
      }
    ],
    "strategy": [
      {
        "q": "Strategy vs switch statements?",
        "a": "Strategy replaces switches with interchangeable algorithms injected/selected at runtime — open for extension."
      },
      {
        "q": "Classic interview example?",
        "a": "PaymentMethod strategies: Card, UPI, Wallet. Pricing strategies for surge."
      },
      {
        "q": "How to choose strategy?",
        "a": "Factory/registry keyed by enum/config. Avoid reflection magic in interviews unless asked."
      }
    ],
    "command": [
      {
        "q": "What does Command enable?",
        "a": "Decouple invoker from action; queue, log, and undo operations. Macro commands batch actions."
      },
      {
        "q": "Undo implementation?",
        "a": "Each command stores enough state to reverse, or use memento. Cleared stack on destructive ops."
      },
      {
        "q": "Example?",
        "a": "Remote control buttons bind to LightOnCommand; GUI actions; game inputs."
      }
    ],
    "state": [
      {
        "q": "State vs strategy?",
        "a": "Strategy is client-chosen algorithm; State transitions change behavior based on internal state machine. Similar structure, different intent."
      },
      {
        "q": "Where to put transitions?",
        "a": "Either in context or in state classes. Be consistent and avoid spaghetti transitions."
      },
      {
        "q": "Example?",
        "a": "Vending machine: Idle → HasMoney → Dispensing. Elevator door states."
      }
    ],
    "template-method": [
      {
        "q": "Template Method vs strategy?",
        "a": "Template Method uses inheritance: base class defines algorithm skeleton with overridable steps. Strategy uses composition for the whole algorithm."
      },
      {
        "q": "Risk?",
        "a": "Fragile base class — subclasses depend on step order. Prefer strategy if steps vary a lot."
      },
      {
        "q": "Example?",
        "a": "DataMiner: mine() calls open/extract/parse/close hooks."
      }
    ],
    "chain-of-responsibility": [
      {
        "q": "How does the chain work?",
        "a": "Each handler decides to process or pass the request. Good for middleware, auth filters, support tickets by severity."
      },
      {
        "q": "Failure mode?",
        "a": "Request falls off the end unhandled — always have a default handler or explicit error."
      },
      {
        "q": "Vs switch of handlers?",
        "a": "Chain is dynamic and ordered; easy to add handlers without editing a central switch."
      }
    ],
    "visitor": [
      {
        "q": "When is Visitor worth it?",
        "a": "Stable object structure, many new operations (export, validate, bill). Avoid when element types change often — visitor breaks."
      },
      {
        "q": "Double dispatch idea?",
        "a": "element.accept(visitor) calls visitor.visitConcrete(this) so the right overload runs."
      },
      {
        "q": "Modern alternative?",
        "a": "Pattern matching / sealed classes in newer languages reduce need for classic Visitor."
      }
    ],
    "repository": [
      {
        "q": "Repository vs DAO?",
        "a": "Repository speaks domain language and may hide multiple DAOs; DAO is often 1:1 with a table. In interviews, repository keeps domain free of SQL."
      },
      {
        "q": "Should repositories return entities or DTOs?",
        "a": "Domain entities/aggregates inside the model; map to DTOs at API edges. Avoid leaking ORM types everywhere."
      },
      {
        "q": "Unit testing benefit?",
        "a": "Mock IRepository to test domain without a database."
      }
    ],
    "tic-tac-toe": [
      {
        "q": "Core classes?",
        "a": "Game, Board, Player, Move, RuleEngine. Keep win-check on Board/RuleEngine not UI."
      },
      {
        "q": "How do you detect win efficiently?",
        "a": "Scan row/col/diag of last move — O(n) not full board each time. For interviews O(n^2) full scan is fine if explained."
      },
      {
        "q": "Extensibility?",
        "a": "N×N boards, custom win-length — parameterize, do not hardcode 3."
      }
    ],
    "coffee-machine": [
      {
        "q": "Model inventory and recipes?",
        "a": "Ingredient inventory; Recipe maps drink→ingredient amounts; Machine validates stock then dispenses and decrements atomically."
      },
      {
        "q": "Concurrency?",
        "a": "If multiple outlets, lock per inventory update or use synchronized dispense queue."
      },
      {
        "q": "Extending drinks?",
        "a": "Add recipe data, not new giant switch — OCP."
      }
    ],
    "vending-machine": [
      {
        "q": "State machine?",
        "a": "Idle → CollectingMoney → SelectItem → Dispense → Change. Reject invalid transitions explicitly."
      },
      {
        "q": "Money handling?",
        "a": "Track inserted amount; compute change via greedy/cashier algorithm; inventory of coins for change."
      },
      {
        "q": "Sold out / exact change?",
        "a": "States or flags that disable selection; clear messages. Show edge-case thinking."
      }
    ],
    "parking-lot": [
      {
        "q": "How do you assign spots?",
        "a": "Strategy: first-fit, closest to elevator, vehicle-size matching. Spot types: S/M/L; vehicle can park in ≥ size."
      },
      {
        "q": "Entry/exit flow?",
        "a": "Ticket on entry with spotId+time; payment on exit based on rate card; free spot. Thread-safe spot allocation map."
      },
      {
        "q": "Multi-floor?",
        "a": "ParkingLot has Floors; each floor has spots. Display boards observe free counts per floor."
      }
    ],
    "elevator": [
      {
        "q": "Pending requests data structure?",
        "a": "Per-elevator direction queues or a scheduler assigning requests to elevators (SCAN/LOOK algorithms). Discuss convoy effect."
      },
      {
        "q": "Single vs multi elevator control?",
        "a": "ElevatorController assigns hall calls to cars based on distance/load. Each car runs its own state machine."
      },
      {
        "q": "Safety?",
        "a": "Door open/close interlocks, emergency stop, overload. Model as hard constraints before optimization."
      }
    ],
    "library-management": [
      {
        "q": "Key entities?",
        "a": "Book, BookItem (barcode), Member, Loan, Reservation, Librarian. Separate catalog book from physical copies."
      },
      {
        "q": "Reservation flow?",
        "a": "When item returned, notify first reservation; hold TTL; expire to next. Avoid double-loan with transactional checks."
      },
      {
        "q": "Search?",
        "a": "Catalog index by ISBN/title/author — may be a service boundary in larger designs."
      }
    ],
    "snake-game": [
      {
        "q": "Model the snake?",
        "a": "Deque of cells; move = add head in direction, remove tail unless growing. Food spawns on empty cell."
      },
      {
        "q": "Collision detection?",
        "a": "Wall and self-hit via set of occupied cells for O(1). Game over state."
      },
      {
        "q": "Tick loop?",
        "a": "Game engine ticks; input changes direction with constraint (no instant reverse)."
      }
    ],
    "chess": [
      {
        "q": "Piece polymorphism?",
        "a": "Piece abstract with possibleMoves(board, pos); King/Knight/... override. Board validates checks."
      },
      {
        "q": "Check/checkmate?",
        "a": "After a move, see if opponent king is attacked; checkmate if no legal move removes check. Expensive but fine for LLD discussion."
      },
      {
        "q": "Special moves?",
        "a": "Castling, en passant, promotion — encode as first-class move rules so you do not forget in design."
      }
    ],
    "atm": [
      {
        "q": "Session flow?",
        "a": "CardRead → PinValidate → SelectTransaction → Execute → Eject. Hardware components as interfaces for testing."
      },
      {
        "q": "Cash dispenser algorithm?",
        "a": "Dispense with available denominations; failure if cannot make amount. Atomic with account debit via bank service."
      },
      {
        "q": "Failure handling?",
        "a": "Network timeout → abort without debit; jam → enter out-of-service; always leave card policy explicit."
      }
    ],
    "inventory-management": [
      {
        "q": "Stock movements?",
        "a": "SKU, Warehouse, StockItem, Reservation, Order. Separate on-hand vs reserved vs available."
      },
      {
        "q": "Concurrency?",
        "a": "Optimistic versioning or row locks when decrementing available. Oversell prevention is the core invariant."
      },
      {
        "q": "Reorder policy?",
        "a": "Threshold events publish to purchasing — keep domain event explicit."
      }
    ],
    "logging-framework": [
      {
        "q": "Core abstractions?",
        "a": "Logger, LogRecord, Appender/Handler, Formatter, Filter, Level. Hierarchy with level inheritance like Log4j."
      },
      {
        "q": "Async logging?",
        "a": "Queue between logger and appenders to not block app threads; discuss overflow policy (block vs drop)."
      },
      {
        "q": "Extensibility?",
        "a": "New appenders without changing logger — Observer/Strategy vibes."
      }
    ],
    "splitwise": [
      {
        "q": "Balance sheet model?",
        "a": "Track net balances per user; expenses create edges. Simplify debts with min-cash-flow heuristic for display."
      },
      {
        "q": "Split types?",
        "a": "Equal, exact, percent — validate sums. Group vs non-group expenses."
      },
      {
        "q": "Simplify debts?",
        "a": "Not always desired (tax/audit). Provide as optional algorithm on net receivables graph."
      }
    ],
    "bookmyshow": [
      {
        "q": "Avoid double booking?",
        "a": "Seat holds with TTL in Redis/DB transaction; confirm payment converts hold→booked. Unique constraint on (showId,seatId)."
      },
      {
        "q": "Search movies/shows?",
        "a": "Catalog service with city/theater indexes; booking service owns seat maps."
      },
      {
        "q": "Payment failure?",
        "a": "Expire hold; release seats; idempotent payment callbacks."
      }
    ]
  },
  "cs-fundamentals": {
    "processes": [
      {
        "q": "Process vs thread?",
        "a": "A process has its own address space, file descriptors, and security identity. Threads share that space with separate stacks/PCs. Processes isolate faults; threads share memory for cheap communication but risk races."
      },
      {
        "q": "What happens on fork()?",
        "a": "Child gets a copy-on-write clone of the parent address space, then typically exec() loads a new program. Mention COW so you do not imply full physical copy upfront."
      },
      {
        "q": "Zombie vs orphan process?",
        "a": "Zombie: exited but parent has not wait()ed — still in the process table. Orphan: parent died; init/systemd adopts and reaps. Interviewers like this classic."
      }
    ],
    "threads": [
      {
        "q": "Thread-per-request vs thread pool?",
        "a": "Thread-per-request is simple but collapses under load (stack memory, context switches). A bounded pool reuses workers and applies backpressure when saturated — prefer pools in servers."
      },
      {
        "q": "User-level vs kernel threads?",
        "a": "Kernel threads are scheduled by the OS onto cores. User-level threads/fibers are multiplexed in-process — cheaper switches but a blocking syscall can stall the mapper. Modern runtimes mix both (goroutines, async)."
      },
      {
        "q": "Why can one bad thread crash the process?",
        "a": "Shared address space — memory corruption or uncaught errors often kill the whole process. That is why browsers use multi-process isolation."
      }
    ],
    "context-switching": [
      {
        "q": "What is saved on a context switch?",
        "a": "CPU registers, program counter, stack pointer, and scheduler metadata; for process switches also memory-management state (page tables/ASID). Cache/TLB warmness is lost — the hidden cost."
      },
      {
        "q": "When is switching too expensive?",
        "a": "High churn with many runnable threads, oversubscription, or frequent blocking. Fix with fewer threads, batching, async I/O, and CPU affinity where appropriate."
      },
      {
        "q": "Voluntary vs involuntary?",
        "a": "Voluntary: thread blocks on I/O/lock. Involuntary: time slice ends or higher priority preempts. Both cost similar save/restore."
      }
    ],
    "cpu-scheduling": [
      {
        "q": "CFS vs round-robin at a high level?",
        "a": "Round-robin gives equal time slices. Linux CFS approximates fair share via a red-black tree of virtual runtime — interactive tasks stay responsive. Realtime classes differ."
      },
      {
        "q": "Priority inversion?",
        "a": "High-priority thread waits on a lock held by low-priority while medium starves the low. Fix with priority inheritance. Classic interview follow-up."
      },
      {
        "q": "I/O-bound vs CPU-bound scheduling?",
        "a": "I/O-bound should get quick CPU bursts to issue next I/O; CPU-bound can take longer slices. Modern schedulers track sleep patterns."
      }
    ],
    "paging-and-virtual-memory": [
      {
        "q": "Why virtual memory?",
        "a": "Gives each process a large contiguous address space, isolation, and demand paging so RAM holds the working set. Enables memory-mapped files and shared libraries."
      },
      {
        "q": "Page fault steps?",
        "a": "MMU miss → trap → kernel finds/loads page (or segfault) → update PTE → resume. Minor faults vs major (disk) matter for latency."
      },
      {
        "q": "Thrashing?",
        "a": "Working set exceeds RAM; system spends time paging not working. Fix: more RAM, fewer processes, or fix memory leaks."
      }
    ],
    "mutex": [
      {
        "q": "Mutex vs spinlock?",
        "a": "Mutex sleeps the thread when contended (good for long critical sections). Spinlock busy-waits (good for very short sections when sleeping costs more). Never hold spinlocks across blocking calls."
      },
      {
        "q": "Reentrant mutex?",
        "a": "Same thread can lock again without deadlock. Useful in callbacks; can hide design smells. Non-recursive is safer default."
      },
      {
        "q": "What is a lost wakeup?",
        "a": "Waiting without proper condition variable patterns. Always wait in a loop checking the predicate under the mutex."
      }
    ],
    "semaphore": [
      {
        "q": "Binary semaphore vs mutex?",
        "a": "Mutex has ownership (unlocker should be locker). Semaphore is a counter for signaling resources and can be posted by another thread — classic producer/consumer."
      },
      {
        "q": "Implement a bounded buffer?",
        "a": "emptyCount + fullCount semaphores plus mutex protecting the buffer structure. Explain order of waits to avoid deadlock."
      },
      {
        "q": "Semaphore pitfalls?",
        "a": "Harder to reason than monitors; easy to mismatch post/wait. Prefer higher-level concurrency utilities when available."
      }
    ],
    "deadlock": [
      {
        "q": "Four Coffman conditions?",
        "a": "Mutual exclusion, hold-and-wait, no preemption, circular wait. Prevention breaks one (e.g., lock ordering breaks circular wait)."
      },
      {
        "q": "Detection vs prevention?",
        "a": "Prevention via ordering/timeouts; detection via wait-for graphs and recovery (kill/rollback). DBs often detect; app code usually prevents."
      },
      {
        "q": "Dining philosophers takeaway?",
        "a": "Naive lock ordering deadlocks; solutions include resource hierarchy, try-lock with backoff, or an arbitrator."
      }
    ],
    "normalization": [
      {
        "q": "1NF 2NF 3NF quickly?",
        "a": "1NF: atomic attributes. 2NF: no partial dependency on part of a composite key. 3NF: no transitive dependency of non-key on non-key. Boyce-Codd is a stricter 3NF."
      },
      {
        "q": "When denormalize?",
        "a": "Read-heavy paths where joins dominate — store derived columns with clear update rules. Document the anomaly you accept."
      },
      {
        "q": "Normalization vs NoSQL modeling?",
        "a": "SQL normalizes then optionally denormalizes; many NoSQL designs denormalize per query pattern from day one."
      }
    ],
    "indexes": [
      {
        "q": "B-tree vs hash index?",
        "a": "B-tree supports ordered scans and range queries; hash supports equality only. Most primary indexes are B-trees."
      },
      {
        "q": "What makes an index unused?",
        "a": "Functions on columns, type mismatches, leading-wildcard LIKEs, low selectivity, or optimizer choosing seq scan on small tables. Use EXPLAIN."
      },
      {
        "q": "Covering index?",
        "a": "Index contains all columns needed — index-only scan. Great for hot read queries; costs write amplification."
      }
    ],
    "joins": [
      {
        "q": "Nested loop vs hash vs merge join?",
        "a": "Nested loop: good for small inner or indexed lookups. Hash: large equality joins. Merge: pre-sorted inputs. Optimizer picks via stats."
      },
      {
        "q": "When do joins explode?",
        "a": "Missing join predicates (cartesian), or many-to-many without aggregation. Always check row estimates in EXPLAIN."
      },
      {
        "q": "Join order importance?",
        "a": "Filter early; put selective tables first when nested. Stats drive this — outdated stats → bad plans."
      }
    ],
    "transactions-and-acid": [
      {
        "q": "ACID in one breath?",
        "a": "Atomicity: all-or-nothing. Consistency: invariants preserved. Isolation: concurrent txns do not trample each other (to a level). Durability: committed data survives crashes."
      },
      {
        "q": "How is durability achieved?",
        "a": "WAL/redo logs flushed to disk before commit ack (with sync settings). Replicas add redundancy but are not a substitute for fsync policy understanding."
      },
      {
        "q": "Long transactions — why bad?",
        "a": "Hold locks/MVCC versions longer, bloat, and raise conflict rates. Keep transactions short and move slow I/O out."
      }
    ],
    "isolation-levels": [
      {
        "q": "Read committed vs repeatable read vs serializable?",
        "a": "RC: each read sees latest committed; non-repeatable reads possible. RR: row set stable snapshot-ish (implementation varies). Serializable: appears as serial order — highest cost."
      },
      {
        "q": "Phantom reads?",
        "a": "New rows match a previously queried predicate. Prevented by serializable or next-key locks / true snapshot isolation depending on engine."
      },
      {
        "q": "What does Postgres default to?",
        "a": "Read Committed by default; RR is snapshot isolation; Serializable is SSI. Knowing your engine beats memorizing generic textbooks alone."
      }
    ],
    "mvcc": [
      {
        "q": "How does MVCC help readers?",
        "a": "Readers see a snapshot without blocking writers (generally). Writers create new row versions. Old versions GC via vacuum/purge."
      },
      {
        "q": "Write skew?",
        "a": "Anomaly under snapshot isolation where two txns write disjoint rows but break a multi-row invariant. Serializable isolation or explicit locks fix it."
      },
      {
        "q": "MVCC downside?",
        "a": "Version bloat, vacuum cost, and larger indexes. Monitor bloat in production answers."
      }
    ],
    "locks": [
      {
        "q": "Row vs table locks?",
        "a": "Row locks allow concurrency; table locks for DDL or bulk ops. Intention locks help engines manage hierarchies."
      },
      {
        "q": "Shared vs exclusive?",
        "a": "Shared for reads in locking schedulers; exclusive for writes. MVCC engines still take locks for writes and some reads under certain levels."
      },
      {
        "q": "Deadlock handling in DB?",
        "a": "Engine detects cycle and aborts one txn (error 1213/deadlock victim). App should retry transaction safely."
      }
    ],
    "execution-plans": [
      {
        "q": "How do you read EXPLAIN?",
        "a": "Look at node types (seq vs index), estimated vs actual rows, and costly sorts/hashes. Start from the node with highest cost time."
      },
      {
        "q": "Why estimates go wrong?",
        "a": "Stale statistics, correlated columns, or hidden functions. Update analyze; consider extended stats."
      },
      {
        "q": "Seq scan always bad?",
        "a": "No — on small tables or low selectivity it can beat index. Argue with numbers, not dogma."
      }
    ],
    "connection-pooling": [
      {
        "q": "Why pool DB connections?",
        "a": "Handshakes/auth are expensive; DB max_connections is limited. Pool multiplexes many app threads onto fewer connections."
      },
      {
        "q": "Pool sizing rule of thumb?",
        "a": "Often closer to (cores * 2) + spindles style formulas for OLTP — not thousands of connections. Too large increases context switching inside DB."
      },
      {
        "q": "PgBouncer transaction vs session pooling?",
        "a": "Transaction pooling reuses connections between txns — highest efficiency but breaks session features. Session pooling is safer for prepared statements/listen."
      }
    ],
    "dns": [
      {
        "q": "Recursive vs authoritative DNS?",
        "a": "Recursive resolvers fetch answers for clients; authoritative servers hold the zone data. TTLs control caching."
      },
      {
        "q": "How does DNS affect failover?",
        "a": "Low TTLs speed failover but raise query load; health-checked VIPs often beat DNS-only failover."
      },
      {
        "q": "DNS spoofing mitigation?",
        "a": "DNSSEC, DoH/DoT, and not trusting DNS alone for security decisions — pin where needed."
      }
    ],
    "tcp": [
      {
        "q": "Three-way handshake?",
        "a": "SYN → SYN-ACK → ACK establishes agreement on sequence numbers. TIME_WAIT exists to handle delayed packets safely."
      },
      {
        "q": "Flow control vs congestion control?",
        "a": "Flow control: receiver window (rwnd). Congestion control: sender probes network (cwnd) with algorithms like CUBIC. Both limit in-flight data."
      },
      {
        "q": "Head-of-line blocking?",
        "a": "Lost packet stalls later data in that TCP connection. HTTP/2 multiplexes streams but still HOL at TCP; HTTP/3/QUIC reduces it."
      }
    ],
    "udp": [
      {
        "q": "When prefer UDP?",
        "a": "Latency-sensitive, loss-tolerant workloads: gaming, VoIP, DNS, QUIC/HTTP3 foundation. You implement reliability if needed."
      },
      {
        "q": "UDP security considerations?",
        "a": "Easy to spoof source IPs — amplification attacks. Validate and rate-limit."
      },
      {
        "q": "UDP vs TCP interview one-liner?",
        "a": "TCP: reliable ordered streams. UDP: datagrams, no connection setup, no delivery guarantee."
      }
    ],
    "tls": [
      {
        "q": "What does TLS provide?",
        "a": "Confidentiality, integrity, and server authentication (client auth optional) via certificates and key exchange."
      },
      {
        "q": "TLS 1.3 improvement?",
        "a": "Fewer round trips (1-RTT, 0-RTT with caveats), removed legacy ciphers, encrypted handshake more fully."
      },
      {
        "q": "Certificate pinning?",
        "a": "App trusts specific cert/SPKI to resist rogue CAs — operational pain on rotation. Mention trade-offs."
      }
    ],
    "https": [
      {
        "q": "HTTPS vs TLS naming?",
        "a": "HTTPS is HTTP over TLS. TLS is the security protocol; HTTPS is the application of it to HTTP (port 443)."
      },
      {
        "q": "Mixed content?",
        "a": "HTTPS page loading HTTP assets — browsers block active mixed content. Serve all assets securely."
      },
      {
        "q": "Termination where?",
        "a": "At LB/edge commonly; mTLS may re-encrypt to services. Know what is encrypted on each hop."
      }
    ],
    "http-http-2-http-3": [
      {
        "q": "HTTP/1.1 vs HTTP/2?",
        "a": "H1: text, one request at a time per connection (or many conns). H2: binary framing, multiplexing, HPACK headers — still over TCP."
      },
      {
        "q": "Why HTTP/3?",
        "a": "Runs on QUIC/UDP — avoids TCP HOL blocking, faster connection establishment. Great on lossy mobile networks."
      },
      {
        "q": "Idempotent methods?",
        "a": "GET/PUT/DELETE are idempotent by spec intent; POST generally not. Critical for safe retries."
      }
    ],
    "rest": [
      {
        "q": "Is REST just JSON over HTTP?",
        "a": "No — resource-oriented URLs, uniform interface, stateless requests, and hypermedia optional (HATEOAS). In practice teams implement pragmatic HTTP APIs."
      },
      {
        "q": "Versioning strategies?",
        "a": "URL path (/v1), header, or content negotiation. Pick one and keep backward compatibility policy clear."
      },
      {
        "q": "Status code discipline?",
        "a": "2xx success, 4xx client, 5xx server. 409 conflicts, 429 limits, 422 validation — shows API maturity."
      }
    ],
    "grpc": [
      {
        "q": "Why gRPC?",
        "a": "Efficient protobuf contracts, codegen stubs, streaming RPCs, and strict schemas. Great for internal service-to-service."
      },
      {
        "q": "gRPC vs REST trade-offs?",
        "a": "gRPC: speed/contracts; browser support historically harder (gRPC-Web). REST: universal HTTP tooling/cacheability."
      },
      {
        "q": "Deadlines?",
        "a": "Always set deadlines/timeouts propagating through call chains to avoid piled-up work."
      }
    ],
    "websocket": [
      {
        "q": "When WebSocket over HTTP polling?",
        "a": "Low-latency bidirectional updates: chat, collab cursors, live quotes. Avoid for rare notifications — SSE/push may suffice."
      },
      {
        "q": "Scaling WebSockets?",
        "a": "Sticky sessions or shared pub/sub so any node can push to a user connection map. Heartbeats detect half-open connections."
      },
      {
        "q": "Security?",
        "a": "Auth on connect, origin checks, size limits, and backpressure — easy to DoS with open sockets."
      }
    ],
    "essential-commands": [
      {
        "q": "How do you find what is using port 8080?",
        "a": "`lsof -i :8080` or `ss -lptn \"sport = :8080\"`. Then inspect the PID."
      },
      {
        "q": "Disk full triage?",
        "a": "`df -h`, `du -xh / | sort -h` (carefully), check inodes (`df -i`), and log rotation. Mention containers filling overlay."
      },
      {
        "q": "Follow a log safely?",
        "a": "`tail -f` / `journalctl -u service -f`. Prefer structured logs and correlation IDs in real systems."
      }
    ],
    "process-management": [
      {
        "q": "systemctl vs bare process?",
        "a": "systemd manages restarts, logs, dependencies, and cgroups. Production services should be supervised."
      },
      {
        "q": "SIGTERM vs SIGKILL?",
        "a": "SIGTERM asks for graceful shutdown; SIGKILL cannot be caught. Always handle SIGTERM to drain connections."
      },
      {
        "q": "Nice/priorities?",
        "a": "nice/renice adjust CPU niceness; do not confuse with realtime priorities. Useful for batch vs latency-sensitive."
      }
    ],
    "memory": [
      {
        "q": "RSS vs virtual size?",
        "a": "VSZ is address space; RSS is resident RAM pages. Large VSZ with small RSS can be fine (reserved maps)."
      },
      {
        "q": "OOM killer?",
        "a": "Kernel kills a process when memory is exhausted — often the largest consumer. Monitor pressure before OOM."
      },
      {
        "q": "How do you debug a leak?",
        "a": "Metrics of RSS over time, heap profiles (pprof/jmap), and container limits. Check native leaks too."
      }
    ],
    "cpu": [
      {
        "q": "Load average meaning?",
        "a": "Runnable+uninterruptible queue length averages. Compare to core count — load 8 on 8 cores ≈ saturated."
      },
      {
        "q": "iowait vs user CPU?",
        "a": "iowait: idle waiting on disk. High user: app compute. High system: syscalls/context. Guides next tool (perf vs iostat)."
      },
      {
        "q": "CPU throttling in containers?",
        "a": "cgroup CFS quotas limit CPU. App may look slow with low utilization — check throttling metrics."
      }
    ],
    "disk-i-o": [
      {
        "q": "Random vs sequential I/O?",
        "a": "Sequential saturates throughput; random stresses IOPS. Databases care about both — WAL sequential, reads often random."
      },
      {
        "q": "Tools?",
        "a": "iostat, iotop, fio for benchmarks. Check await/svctm and queue depth."
      },
      {
        "q": "fsync why it matters?",
        "a": "Durability requires fsync; disabling it gains speed and risks data loss — relate to DB durability settings."
      }
    ],
    "networking-tools": [
      {
        "q": "curl vs dig vs ss?",
        "a": "curl for HTTP; dig/nslookup for DNS; ss/netstat for sockets; traceroute/mtr for path; tcpdump/Wireshark for packets."
      },
      {
        "q": "How do you debug \"service unreachable\"?",
        "a": "DNS resolve → TCP connect → TLS → HTTP. Isolate which layer fails. Check security groups and listen address."
      },
      {
        "q": "tcpdump one-liner value?",
        "a": "Prove SYN leaves and SYN-ACK returns — separates app bugs from network policy."
      }
    ],
    "race-conditions": [
      {
        "q": "Define a race condition.",
        "a": "Correctness depends on timing of concurrent operations on shared state. Example: check-then-act without locks."
      },
      {
        "q": "How do you detect them?",
        "a": "Code review of shared mutable state, stress tests, TSAN/race detectors, and reproducing under load."
      },
      {
        "q": "Fix patterns?",
        "a": "Immutability, confinement, locks/atomics, or message passing. Prefer eliminating shared mutation."
      }
    ],
    "synchronization": [
      {
        "q": "Monitor/condition variable pattern?",
        "a": "Mutex + condition: wait while predicate false; signal on change; always re-check predicate (spurious wakeups)."
      },
      {
        "q": "ReadWriteLock use case?",
        "a": "Many readers rare writers. Write-heavy workloads often do worse than plain mutex."
      },
      {
        "q": "Avoiding deadlocks when syncing?",
        "a": "Global lock order, try-lock with backoff, reduce lock scope, prefer lock-free structures carefully."
      }
    ],
    "concurrency-locks": [
      {
        "q": "Fair vs unfair locks?",
        "a": "Fair locks reduce starvation but lower throughput. Unfair allows barging — common default."
      },
      {
        "q": "Optimistic concurrency?",
        "a": "Version checks / CAS instead of holding locks — great for low contention. Retry on conflict."
      },
      {
        "q": "Lock granularity advice?",
        "a": "Too coarse kills parallelism; too fine risks deadlock and overhead. Start coarse, split with measurements."
      }
    ],
    "thread-pool": [
      {
        "q": "How to size a pool?",
        "a": "CPU-bound ≈ cores (+ small). I/O-bound can be larger but capped; use queue + rejection policy. Measure latency under load."
      },
      {
        "q": "Unbounded queues danger?",
        "a": "Memory blowup hiding overload. Prefer bounded queues with clear rejection/backpressure."
      },
      {
        "q": "Separation of pools?",
        "a": "Isolate latency-critical work from batch so one cannot starve the other."
      }
    ],
    "executors": [
      {
        "q": "Java ExecutorService essentials?",
        "a": "submit/execute, shutdown/shutdownNow, futures, and invokeAll. Prefer factory methods with explicit limits over cached unbounded."
      },
      {
        "q": "Future vs CompletableFuture?",
        "a": "CompletableFuture composes async pipelines; plain Future is get/cancel. Know exception propagation."
      },
      {
        "q": "What happens on reject?",
        "a": "CallerRuns, Abort, Discard — pick intentionally. CallerRuns applies natural backpressure."
      }
    ],
    "async-await": [
      {
        "q": "Async ≠ parallel.",
        "a": "Async frees a thread while waiting on I/O; CPU work still needs threads/processes for parallelism. Clarify in interviews."
      },
      {
        "q": "Pitfall: blocking inside async?",
        "a": "Blocking a small event-loop pool freezes everything. Offload blocking calls to a worker pool."
      },
      {
        "q": "Cancellation?",
        "a": "Propagate cancellation tokens/contexts; otherwise work continues wasting resources."
      }
    ],
    "actors-and-message-passing": [
      {
        "q": "Actor model benefit?",
        "a": "No shared mutable state — actors process messages sequentially, communicate by messages. Great for isolation and distribution (Erlang/Akka)."
      },
      {
        "q": "At-most-once vs at-least-once messaging?",
        "a": "Same as queues: choose delivery and make handlers idempotent when needed."
      },
      {
        "q": "When not actors?",
        "a": "Simple request/response CRUD may not need the complexity. Use when concurrency/isolation is central."
      }
    ],
    "authentication": [
      {
        "q": "Authn vs authz?",
        "a": "Authentication: who are you. Authorization: what may you do. Sessions/JWT prove identity; RBAC/ABAC decide permissions."
      },
      {
        "q": "Session cookies vs tokens?",
        "a": "Cookies with HttpOnly/Secure/SameSite fit browsers; bearer tokens fit APIs/mobile. CSRF matters for cookies."
      },
      {
        "q": "Password storage?",
        "a": "Slow hashes (bcrypt/argon2), unique salts, never reversible encryption for passwords."
      }
    ],
    "jwt": [
      {
        "q": "What goes in a JWT?",
        "a": "Signed claims: sub, exp, iss, roles maybe. Keep payloads small; do not store secrets in the payload (it is readable)."
      },
      {
        "q": "Logout/revocation problem?",
        "a": "Stateless JWTs cannot be revoked easily — use short TTL + refresh tokens/blocklist/versioned sessions."
      },
      {
        "q": "alg=none / key confusion?",
        "a": "Always verify algorithm allow-list and use proper libraries. Historical CVEs matter in security interviews."
      }
    ],
    "oauth": [
      {
        "q": "OAuth vs OpenID Connect?",
        "a": "OAuth is authorization delegation; OIDC adds identity (id_token) on top. Login with Google is usually OIDC."
      },
      {
        "q": "Authorization code + PKCE why?",
        "a": "Keeps tokens off the front channel; PKCE protects public clients from code interception."
      },
      {
        "q": "When use client credentials?",
        "a": "Machine-to-machine with no user — service accounts. Do not use it for user login."
      }
    ],
    "rbac": [
      {
        "q": "RBAC vs ABAC?",
        "a": "RBAC: roles→permissions. ABAC: attributes/policies (time, resource tags). RBAC is simpler; ABAC is flexible/complex."
      },
      {
        "q": "Role explosion?",
        "a": "Too many bespoke roles. Prefer fewer roles + resource scopes, or ABAC for exceptions."
      },
      {
        "q": "Where to enforce?",
        "a": "Server-side on every request — never only in UI. Central policy engine helps microservices."
      }
    ],
    "caching": [
      {
        "q": "Cache invalidation strategies?",
        "a": "TTL, explicit delete on write, versioned keys. Choose based on consistency tolerance."
      },
      {
        "q": "Local vs distributed cache?",
        "a": "Local (in-process) is fastest but inconsistent across nodes; Redis is shared. Often tier both."
      },
      {
        "q": "Stampede prevention?",
        "a": "Singleflight, soft TTL, staggered expiry — same as HLD caching answers with code-level detail."
      }
    ],
    "redis": [
      {
        "q": "Redis single-threaded — still fast?",
        "a": "Commands run on one thread avoiding lock complexity; I/O and speed come from in-memory + efficient structures. Long commands block others — avoid O(N) keys unexpectedly."
      },
      {
        "q": "Persistence modes?",
        "a": "RDB snapshots, AOF, or none (pure cache). Match durability to use case."
      },
      {
        "q": "Distributed lock caveat?",
        "a": "Redlock is debated; prefer Redis locks with short TTL + fencing tokens, or a consensus store for strong correctness."
      }
    ],
    "rate-limiter": [
      {
        "q": "Implement token bucket in Redis?",
        "a": "Lua script atomically refills tokens based on time and consumes cost. Return remaining + retry-after."
      },
      {
        "q": "Fail open or closed?",
        "a": "Fail open for availability-sensitive reads; fail closed for auth/SMS abuse prevention. State the product choice."
      },
      {
        "q": "Distributed vs local limiter?",
        "a": "Local is approximate under many nodes; Redis gives global accuracy with latency cost."
      }
    ],
    "circuit-breaker": [
      {
        "q": "States?",
        "a": "Closed (normal) → Open (fail fast after threshold) → Half-open (trial requests). Prevents cascading failures."
      },
      {
        "q": "How to set thresholds?",
        "a": "Based on error rate/latency SLOs and dependency criticality. Too sensitive flails; too dull protects nothing."
      },
      {
        "q": "Breaker vs retry?",
        "a": "Retries help transient blips; breakers stop hammering a sick dependency. Use both carefully."
      }
    ],
    "distributed-lock": [
      {
        "q": "Correctness requirements?",
        "a": "Mutual exclusion, lock expiry/TTL to survive crashes, and fencing tokens so old lock holders cannot commit after expiry."
      },
      {
        "q": "Redis vs ZooKeeper/etcd?",
        "a": "etcd/ZK via consensus are safer for strong locks; Redis is faster but easier to get subtly wrong."
      },
      {
        "q": "Do you always need one?",
        "a": "Often uniqueness constraints or leader election suffice. Locks are coordination — minimize holding time."
      }
    ],
    "kafka": [
      {
        "q": "Offset commit strategies?",
        "a": "Commit after successful side effects for at-least-once. Auto-commit can lose/duplicate. Exactly-once needs transactional API + idempotent sinks."
      },
      {
        "q": "Partition key choice?",
        "a": "Key for ordering (orderId); null for round-robin throughput. Hot keys overload a partition."
      },
      {
        "q": "Lag meaning?",
        "a": "Consumer behind log end offset — scale consumers (up to #partitions) or optimize processing."
      }
    ],
    "rabbitmq": [
      {
        "q": "ACK modes?",
        "a": "Auto ack risks loss on crash; manual ack after persistence/side effects. Prefetch controls in-flight messages."
      },
      {
        "q": "Exchange types?",
        "a": "Direct, topic, fanout, headers — routing flexibility is Rabbit's strength vs Kafka's log model."
      },
      {
        "q": "Quorum queues?",
        "a": "Replicated queues for durability/availability — prefer over classic mirrored queues in modern Rabbit."
      }
    ],
    "docker": [
      {
        "q": "Image vs container?",
        "a": "Image is immutable layers; container is a running instance with writable layer. Entrypoint/CMD define start."
      },
      {
        "q": "Why PID 1 matters?",
        "a": "PID 1 must reap zombies and handle signals — use a proper init or runtime that proxies SIGTERM."
      },
      {
        "q": "Security basics?",
        "a": "Non-root user, minimal base images, no secrets in layers, read-only FS where possible, resource limits."
      }
    ],
    "kubernetes": [
      {
        "q": "Pod vs deployment vs service?",
        "a": "Pod: atomic scheduling unit. Deployment: desired replicas/rollouts. Service: stable networking to pods."
      },
      {
        "q": "Liveness vs readiness?",
        "a": "Liveness: restart if broken. Readiness: stop sending traffic until warm. Misusing liveness causes restart storms."
      },
      {
        "q": "Config/secrets?",
        "a": "ConfigMaps/Secrets mounted as env/files; prefer files for secrets rotation and avoid logging env."
      }
    ],
    "microservices": [
      {
        "q": "When not to use microservices?",
        "a": "Small team/unclear domains — modular monolith is often better. Microservices demand platform maturity."
      },
      {
        "q": "Service boundary heuristic?",
        "a": "Around business capabilities with independent deploy/data. Avoid distributed monolith of chatty RPCs."
      },
      {
        "q": "Data ownership?",
        "a": "Each service owns its DB; integrate via APIs/events. No shared tables across services."
      }
    ],
    "saga": [
      {
        "q": "Choreography vs orchestration?",
        "a": "Choreography: services react to events. Orchestration: a coordinator drives steps. Orchestration is easier to observe; choreography is looser coupled."
      },
      {
        "q": "Compensation?",
        "a": "Undo steps when a later step fails (refund, release inventory). Compensations must be idempotent."
      },
      {
        "q": "Saga vs 2PC?",
        "a": "Saga avoids long distributed locks and is more available; provides eventual consistency, not full ACID across services."
      }
    ],
    "cqrs": [
      {
        "q": "What problem does CQRS solve?",
        "a": "Different read/write models — optimize each. Writes enforce invariants; reads use denormalized views."
      },
      {
        "q": "Do you need event sourcing with CQRS?",
        "a": "No — they pair well but CQRS can use ordinary write DB + projected read DB."
      },
      {
        "q": "Consistency lag?",
        "a": "Read models may lag writes — UX must tolerate or read-your-writes from write model when needed."
      }
    ],
    "event-sourcing": [
      {
        "q": "Event sourcing idea?",
        "a": "Store immutable events as source of truth; fold to current state. Enables audit/time-travel and projections."
      },
      {
        "q": "Hard parts?",
        "a": "Schema evolution, GDPR erasure, and eventual read models. Not default for every CRUD app."
      },
      {
        "q": "Snapshotting?",
        "a": "Periodically store folded state to avoid replaying millions of events on load."
      }
    ],
    "observability": [
      {
        "q": "Three pillars?",
        "a": "Logs, metrics, traces — plus continuous profiling in modern setups. Correlate with request IDs."
      },
      {
        "q": "Golden signals?",
        "a": "Latency, traffic, errors, saturation (Google SRE). Alert on symptoms users feel."
      },
      {
        "q": "Cardinality explosion?",
        "a": "High-cardinality labels (userId) blow metric systems. Keep metrics coarse; put IDs in logs/traces."
      }
    ],
    "structured-logging": [
      {
        "q": "Why JSON logs?",
        "a": "Machine-parseable fields for search/aggregation. Include level, service, traceId, and error codes."
      },
      {
        "q": "What not to log?",
        "a": "Secrets, PANs, raw PII without policy. Redact at source."
      },
      {
        "q": "Log levels discipline?",
        "a": "Info for milestones, warn for degraded, error for failed requests — avoid info spam."
      }
    ],
    "prometheus": [
      {
        "q": "Pull model benefit?",
        "a": "Prometheus scrapes targets — simplifies clients and service discovery. Pushgateway for short jobs."
      },
      {
        "q": "Counter vs gauge vs histogram?",
        "a": "Counter increases; gauge up/down; histogram samples distributions (latency). Use histograms for SLOs."
      },
      {
        "q": "PromQL tip?",
        "a": "rate() on counters; histogram_quantile on buckets. Avoid averaging averages."
      }
    ],
    "grafana": [
      {
        "q": "Dashboard advice?",
        "a": "Start from user journeys/golden signals, not every metric. Templating by service/env. Link panels to runbooks."
      },
      {
        "q": "Alerting in Grafana vs Prometheus?",
        "a": "Either works; keep alert definitions close to metrics (Prometheus/Alertmanager) for reviewability."
      },
      {
        "q": "RED method?",
        "a": "Rate, Errors, Duration for services — complements USE for resources."
      }
    ],
    "opentelemetry": [
      {
        "q": "What does OTel standardize?",
        "a": "APIs/SDKs for traces/metrics/logs and context propagation so you are not vendor-locked."
      },
      {
        "q": "Context propagation?",
        "a": "W3C traceparent headers across HTTP/gRPC so one request becomes one trace."
      },
      {
        "q": "Sampling?",
        "a": "Head/tail sampling control cost. Always sample errors and slow traces if possible."
      }
    ]
  },
  "behavioral": {
    "star-method": [
      {
        "q": "What is STAR?",
        "a": "Situation, Task, Action, Result. Keep Situation/Task short (~20%), Action detailed with your decisions (~60%), Result quantified (~20%). Practice aloud under 2–3 minutes."
      },
      {
        "q": "Common STAR failure?",
        "a": "We-we-we without your actions, or no measurable result. Use \"I\" for your contribution and numbers for impact."
      },
      {
        "q": "How many stories?",
        "a": "About 20–25 covering failure, conflict, leadership, ownership, ambiguity, and customer impact so you can remap to any LP."
      }
    ],
    "building-your-story-bank-25": [
      {
        "q": "How do you build the bank?",
        "a": "Spreadsheet: story title, situation, your actions, metrics, LPs/themes tagged, and a 45-second version plus a 3-minute version."
      },
      {
        "q": "What if I am junior with few stories?",
        "a": "Use class projects, internships, open source, on-call shadows, and mentoring classmates — emphasize judgment and learning velocity."
      },
      {
        "q": "Reuse across companies?",
        "a": "Yes — retarget the same story to different principles by emphasizing different actions/results."
      }
    ],
    "amazon-leadership-principles": [
      {
        "q": "How do you prep LPs?",
        "a": "Map 2 stories to each high-frequency LP (Ownership, Customer Obsession, Deliver Results, Dive Deep, Bias for Action, Disagree and Commit). Practice follow-ups."
      },
      {
        "q": "What does Dive Deep sound like?",
        "a": "You show data you pulled, hypotheses you tested, and what you found — not vague \"I investigated.\""
      },
      {
        "q": "Disagree and Commit example shape?",
        "a": "You disagreed with data, escalated respectfully, then fully committed to the decision and owned the outcome."
      }
    ],
    "googles-behavioral-interview-guidance": [
      {
        "q": "What does Google optimize for?",
        "a": "General cognitive ability, leadership, role-related knowledge, and \"Googleyness\" — collaboration, comfort with ambiguity, humility."
      },
      {
        "q": "How is it different from Amazon?",
        "a": "Less rigid LP checklist, more open problem-solving and teaming. Still use STAR; emphasize learning and impact."
      },
      {
        "q": "Hypothetical vs past behavior?",
        "a": "Prefer real stories; if hypothetical, structure clearly and state assumptions."
      }
    ],
    "ownership": [
      {
        "q": "Ownership story checklist?",
        "a": "You saw a gap outside your job description, took responsibility end-to-end, unblocked others, and measured the outcome."
      },
      {
        "q": "Follow-up they ask?",
        "a": "\"What did you personally do?\" and \"What would you do differently?\" — prepare both."
      },
      {
        "q": "Anti-pattern?",
        "a": "Taking credit for a team win without your concrete actions."
      }
    ],
    "leadership": [
      {
        "q": "Explain leadership as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for leadership?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after leadership?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect leadership to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "conflict": [
      {
        "q": "Explain conflict as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for conflict?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after conflict?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect conflict to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "failure-and-learning": [
      {
        "q": "Explain failure and learning as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for failure and learning?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after failure and learning?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect failure and learning to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "production-incident": [
      {
        "q": "Explain production incident as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for production incident?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after production incident?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect production incident to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "mentoring": [
      {
        "q": "Explain mentoring as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for mentoring?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after mentoring?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect mentoring to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "promotion-and-growth": [
      {
        "q": "Explain promotion and growth as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for promotion and growth?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after promotion and growth?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect promotion and growth to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "scaling-a-system-or-team": [
      {
        "q": "Explain scaling a system or team as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for scaling a system or team?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after scaling a system or team?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect scaling a system or team to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "customer-obsession": [
      {
        "q": "Explain customer obsession as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for customer obsession?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after customer obsession?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect customer obsession to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "bias-for-action": [
      {
        "q": "Explain bias for action as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for bias for action?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after bias for action?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect bias for action to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "dive-deep": [
      {
        "q": "Explain dive deep as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for dive deep?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after dive deep?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect dive deep to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "disagree-and-commit": [
      {
        "q": "Explain disagree and commit as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for disagree and commit?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after disagree and commit?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect disagree and commit to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "deliver-results": [
      {
        "q": "Explain deliver results as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for deliver results?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after deliver results?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect deliver results to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ],
    "innovation": [
      {
        "q": "Explain innovation as you would in an interview.",
        "a": "Start with a precise definition, then the problem it solves, then one concrete example from production systems. Close with trade-offs and a common pitfall. Structure beats buzzwords."
      },
      {
        "q": "What trade-offs should you mention for innovation?",
        "a": "Discuss cost vs performance, complexity vs reliability, and operational burden. Interviewers hire for judgment — name what you would monitor after shipping."
      },
      {
        "q": "What follow-up questions come after innovation?",
        "a": "Expect \"what fails?\", \"how do you test?\", and \"how does this change at 10× scale?\". Prepare one failure mode and one metric for each."
      },
      {
        "q": "How do you connect innovation to a larger design?",
        "a": "Tie it to requirements (latency, consistency, cost), show where it sits in the diagram, and say what you would build first vs later."
      }
    ]
  }
};

export const getInterviewQuestions = (trackId, topicId) =>
  interviewQuestions[trackId]?.[topicId] || null;
